/**
 * Spec 216 PR4 — Avatar primitive.
 *
 * Renders a circular badge with 1-2 letter initials over the
 * `--c-primary` fill, using the `--c-on-primary` token introduced in
 * spec 200's a11y closeout so the contrast clears AA in both light
 * (#fff on navy) and dark (#0f1923 on cyan) themes.
 *
 * Sizes follow the icon scale documented in DESIGN_SYSTEM.md §9
 * (sm 24, md 36, lg 48 px). Pick `sm` for inline mentions in lists,
 * `md` for the sidebar identity / chips, `lg` for empty-state heroes
 * or detail headers.
 */
import './Avatar.css';

export type AvatarSize = 'sm' | 'md' | 'lg';

const SIZE_PX: Record<AvatarSize, number> = { sm: 24, md: 36, lg: 48 };

export interface AvatarProps {
  /** Full display name. The component derives initials from it. */
  name: string;
  size?: AvatarSize;
  /** Native `title` for hover tooltips (collapsed sidebars). */
  title?: string;
  /**
   * When the surrounding text already names the user, hide the
   * avatar from assistive tech to avoid the screen reader saying
   * "AF AdminFront AdminFront".
   */
  ariaHidden?: boolean;
}

function initialsOf(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean).slice(0, 2);
  const out = parts.map((p) => p[0]?.toUpperCase() ?? '').join('');
  return out || '?';
}

export function Avatar({
  name,
  size = 'md',
  title,
  ariaHidden = false,
}: AvatarProps) {
  const px = SIZE_PX[size];
  return (
    <span
      className={`avatar avatar--${size}`}
      style={{ width: px, height: px }}
      title={title}
      aria-hidden={ariaHidden ? 'true' : undefined}
      aria-label={!ariaHidden ? name : undefined}
      role={ariaHidden ? undefined : 'img'}
      data-testid="avatar"
    >
      {initialsOf(name)}
    </span>
  );
}
